import React, { ChangeEvent, FocusEvent, useContext, useState } from "react";

import { ButtonProps } from "@mui/material";
import { Button } from "nhsuk-react-components";
import styled from "styled-components";
import DTEInput from "../UI/DTEInput/DTEInput";
import { baseButton } from "../UI/DTEButton/DTEButton";
import { ContentContext } from "../../../context/ContentContext";

type Props = {
  id?: string;
  buttonAriaLabelShow?: string;
  buttonAriaLabelHide?: string;
  name?: string;
  label?: string;
  error?: string;
  hint?: string;
  required?: boolean;
  value?: string;
  disabled?: boolean;
  onValueChange?: (value: ChangeEvent<HTMLInputElement>) => void;
  onValueBlur?: (value: FocusEvent<HTMLInputElement>) => void;
  autoFocus?: boolean;
  autocomplete?: string;
  spellcheck?: boolean;
};

type StyledButtonProps = {
  error?: string;
} & ButtonProps;

const StyledDiv = styled.div`
  position: relative;
`;

const StyledButton = styled(Button)<StyledButtonProps>`
  position: absolute;
  display: block;
  right: 0;
  bottom: ${({ error }) => (error ? "1px" : "0")};
  height: 2.15em;
  padding: 0;
  min-width: fit-content;
  &&:active {
    width: 95px;
    top: unset;
  }
  &&:focus {
    width: 95px;
    top: unset;
  }
  & {
    ${baseButton}
  }
  && {
    min-height: 40px;
    padding-left: 0;
    padding-right: 0;
    margin-inline: auto;
    width: 95px;
  }
`;

function PasswordShowHide({
  id,
  buttonAriaLabelShow,
  buttonAriaLabelHide,
  name,
  label,
  error,
  hint,
  required,
  onValueChange,
  onValueBlur,
  value,
  disabled,
  autoFocus,
  autocomplete,
  spellcheck,
}: Props) {
  const [showPassword, setShowPassword] = useState(false);
  const { content } = useContext(ContentContext);
  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  return (
    <StyledDiv>
      <DTEInput
        id={id}
        name={name}
        label={label}
        error={error}
        hint={hint}
        required={required}
        aria-controls="password"
        aria-describedby={`${id}--error-message`}
        aria-required={required}
        aria-invalid
        disabled={disabled}
        value={value}
        type={showPassword ? "text" : "password"}
        onValueChange={onValueChange}
        onValueBlur={onValueBlur}
        autoFocus={autoFocus}
        autocomplete={autocomplete}
        spellcheck={spellcheck}
      />
      <StyledButton
        type="button"
        className="nhsuk-button--secondary"
        onClick={handleClickShowPassword}
        aria-label={showPassword ? buttonAriaLabelHide : buttonAriaLabelShow}
        error={error}
      >
        {showPassword ? content["password-button-hide"] : content["password-button-show"]}
      </StyledButton>

      <span className="nhsuk-u-visually-hidden" aria-live="polite">
        {showPassword ? content["password-button-hide-aria"] : content["password-button-show-aria"]}
      </span>
    </StyledDiv>
  );
}

export default PasswordShowHide;
